// pages/secure/AccountingPage.js
// The brokerage's operational accounting desk: record money in/out with a
// simplified double-entry form, and read auto-calculated financial statements
// (P&L, balance sheet, budget vs actual, tax summary). Modal-driven CRUD.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const thisYear = new Date().getFullYear();

// Simple money-in / money-out shortcuts that build a balanced 2-line entry:
// Deposit/income: debit Operating Bank (1000), credit the income account.
// Expense/payment: debit the expense account, credit Operating Bank (1000).
const CATEGORIES = [
    { key: 'rent_receipt', label: 'Rent / receipt received', flow: 'in' },
    { key: 'commission', label: 'Commission income', flow: 'in' },
    { key: 'deposit', label: 'Other deposit', flow: 'in' },
    { key: 'operating_expense', label: 'Operating expense', flow: 'out' },
    { key: 'salary', label: 'Salary / payroll', flow: 'out' },
    { key: 'payment', label: 'Vendor payment', flow: 'out' },
    { key: 'owner_draw', label: 'Owner draw', flow: 'out' },
    { key: 'withdrawal', label: 'Other withdrawal', flow: 'out' },
];

const TABS = ['Ledger', 'Income (P&L)', 'Balance Sheet', 'Budget', 'Tax Summary'];

const AccountingPage = () => {
    const apiClient = useApiClient();
    const [tab, setTab] = useState('Ledger');
    const [accounts, setAccounts] = useState([]);
    const [entries, setEntries] = useState([]);
    const [error, setError] = useState('');
    const [year, setYear] = useState(thisYear);

    // Modals
    const [adding, setAdding] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Statement data
    const [income, setIncome] = useState(null);
    const [balance, setBalance] = useState(null);
    const [budget, setBudget] = useState(null);
    const [tax, setTax] = useState(null);

    const bankAccount = accounts.find(a => a.code === '1000');

    const loadCore = useCallback(() => {
        Promise.all([apiClient.get('/api/accounting/accounts'), apiClient.get('/api/accounting/entries')])
            .then(([acc, ent]) => { setAccounts(acc); setEntries(ent); })
            .catch(err => setError(err.message));
    }, [apiClient]);

    useEffect(() => { loadCore(); }, [loadCore]);

    useEffect(() => {
        setError('');
        if (tab === 'Income (P&L)') apiClient.get(`/api/accounting/statements/income?year=${year}`).then(setIncome).catch(e => setError(e.message));
        if (tab === 'Balance Sheet') apiClient.get(`/api/accounting/statements/balance-sheet?as_of=${year}-12-31`).then(setBalance).catch(e => setError(e.message));
        if (tab === 'Budget') apiClient.get(`/api/accounting/budget?year=${year}`).then(setBudget).catch(e => setError(e.message));
        if (tab === 'Tax Summary') apiClient.get(`/api/accounting/statements/tax-summary?year=${year}`).then(setTax).catch(e => setError(e.message));
    }, [tab, year, apiClient]);

    const openAdd = () => {
        setAdding({ entry_date: new Date().toISOString().slice(0, 10), category: 'operating_expense', amount: '', memo: '', reference: '', account_id: '' });
    };

    const saveEntry = async () => {
        const cat = CATEGORIES.find(c => c.key === adding.category);
        const amt = Number(adding.amount);
        if (!amt || amt <= 0) { setError('Enter an amount greater than zero.'); return; }
        if (!adding.account_id) { setError('Choose the income/expense account.'); return; }
        if (!bankAccount) { setError('No Operating Bank Account (code 1000) in the chart of accounts.'); return; }
        // Build the two balanced lines
        const lines = cat.flow === 'in'
            ? [{ account_id: bankAccount.account_id, debit: amt, credit: 0 }, { account_id: adding.account_id, debit: 0, credit: amt }]
            : [{ account_id: adding.account_id, debit: amt, credit: 0 }, { account_id: bankAccount.account_id, debit: 0, credit: amt }];
        setIsSaving(true);
        setError('');
        try {
            await apiClient.post('/api/accounting/entries', {
                entry_date: adding.entry_date, memo: adding.memo, category: adding.category,
                reference: adding.reference, lines,
            });
            setAdding(null);
            loadCore();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const openView = async (id) => {
        try { setViewing(await apiClient.get(`/api/accounting/entries/${id}`)); }
        catch (err) { setError(err.message); }
    };

    // Only income & expense accounts are pickable in the simplified form
    const pickable = accounts.filter(a => {
        const cat = CATEGORIES.find(c => c.key === adding?.category);
        return cat?.flow === 'in' ? a.type === 'income' : a.type === 'expense';
    });

    return (
        <div className="p-6 max-w-6xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Accounting</h1>
                <div className="flex items-center gap-3">
                    <select value={year} onChange={e => setYear(Number(e.target.value))} className="border rounded-md px-2 py-1.5 text-sm">
                        {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button onClick={openAdd} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">+ Record entry</button>
                </div>
            </div>
            <p className="text-slate-500 mb-4">The brokerage's operational books — kept entirely separate from client escrow. Statements calculate automatically.</p>

            <div className="flex gap-1 border-b mb-4 flex-wrap">
                {TABS.map(t => (
                    <button key={t} onClick={() => setTab(t)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                        {t}
                    </button>
                ))}
            </div>

            {error && <p className="mb-4 text-red-500">{error}</p>}

            {/* LEDGER */}
            {tab === 'Ledger' && (
                <div className="bg-white rounded-lg shadow p-3">
                    <DataList rows={entries} rowKey="gl_entry_id" csvName="gl-journal" onRowClick={(e) => openView(e.gl_entry_id)}
                        defaultSort={{ key: 'entry_date', dir: 'desc' }} empty="No entries yet — record the first one."
                        note={entries.length >= 500 ? 'Searching the 500 newest entries loaded.' : undefined}
                        filters={[{ key: 'category', label: 'Category' }]}
                        columns={[
                            { key: 'entry_date', label: 'Date', type: 'date', value: (e) => (e.entry_date ? String(e.entry_date).slice(0, 10) : null) },
                            { key: 'gl_entry_id', label: 'Entry', type: 'number', render: (e) => <span className="text-xs text-slate-500">#{e.gl_entry_id}</span> },
                            { key: 'category', label: 'Category', value: (e) => String(e.category || '').replace(/_/g, ' '),
                                render: (e) => <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 whitespace-nowrap">{String(e.category || '').replace(/_/g, ' ')}</span> },
                            { key: 'reference', label: 'Reference' },
                            { key: 'memo', label: 'Memo', render: (e) => e.memo || <span className="text-slate-400">(no memo)</span> },
                            { key: 'total', label: 'Amount', type: 'number', render: (e) => <span className="font-medium text-slate-800">{fmt(e.total)}</span> },
                        ]} />
                </div>
            )}

            {/* INCOME STATEMENT */}
            {tab === 'Income (P&L)' && income && (
                <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
                    <h3 className="font-semibold text-slate-700 mb-2">Income</h3>
                    <StatementRows rows={income.income} />
                    <Row label="Total Income" value={income.total_income} bold />
                    <h3 className="font-semibold text-slate-700 mt-4 mb-2">Expenses</h3>
                    <StatementRows rows={income.expenses} />
                    <Row label="Total Expenses" value={income.total_expense} bold />
                    <div className="border-t-2 mt-3 pt-3">
                        <Row label={`Net Income ${income.year}`} value={income.net_income} bold big
                             positive={income.net_income >= 0} />
                    </div>
                </div>
            )}

            {/* BALANCE SHEET */}
            {tab === 'Balance Sheet' && balance && (
                <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
                    <h3 className="font-semibold text-slate-700 mb-2">Assets</h3>
                    <StatementRows rows={balance.assets} />
                    <Row label="Total Assets" value={balance.total_assets} bold />
                    <h3 className="font-semibold text-slate-700 mt-4 mb-2">Liabilities</h3>
                    <StatementRows rows={balance.liabilities} />
                    <Row label="Total Liabilities" value={balance.total_liabilities} bold />
                    <h3 className="font-semibold text-slate-700 mt-4 mb-2">Equity</h3>
                    <StatementRows rows={balance.equity} />
                    <Row label="Retained Earnings" value={balance.retained_earnings} />
                    <Row label="Total Equity" value={balance.total_equity} bold />
                    <div className="border-t-2 mt-3 pt-3 text-sm">
                        {balance.balanced
                            ? <p className="text-green-600">✓ Balanced — Assets = Liabilities + Equity</p>
                            : <p className="text-amber-600">⚠ Out of balance — review entries</p>}
                    </div>
                </div>
            )}

            {/* BUDGET */}
            {tab === 'Budget' && budget && (
                <div className="bg-white rounded-lg shadow p-3 max-w-3xl">
                    <DataList rows={budget.rows} rowKey="account_id" csvName={`budget-vs-actual-${budget.year}`} defaultSort={{ key: 'account', dir: 'asc' }}
                        empty={`No income or expense activity yet for ${budget.year}.`}
                        columns={[
                            { key: 'account', label: 'Account', value: (r) => `${r.code} ${r.name}`, sortValue: (r) => String(r.code) },
                            { key: 'budget', label: 'Budget', type: 'number', render: (r) => fmt(r.budget) },
                            { key: 'actual', label: 'Actual', type: 'number', render: (r) => fmt(r.actual) },
                            { key: 'variance', label: 'Variance', type: 'number', render: (r) => <span className={r.variance < 0 ? 'text-red-500' : 'text-green-600'}>{fmt(r.variance)}</span> },
                        ]} />
                </div>
            )}

            {/* TAX SUMMARY */}
            {tab === 'Tax Summary' && tax && (
                <div className="bg-white rounded-lg shadow p-6 max-w-xl">
                    <p className="text-slate-500 text-sm mb-3">Category totals for {tax.year} — hand these to your accountant.</p>
                    {tax.categories.length === 0 && <p className="text-slate-500">No activity yet for {tax.year}.</p>}
                    {tax.categories.map(c => (
                        <div key={c.category} className="flex justify-between py-1.5 border-b text-sm">
                            <span className="text-slate-700 capitalize">{c.category.replace(/_/g, ' ')} <span className="text-slate-400">({c.entries})</span></span>
                            <span className="font-medium">{fmt(c.net)}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Add-entry modal */}
            <Modal open={!!adding} onClose={() => setAdding(null)} title="Record a money entry"
                   footer={<>
                       <button onClick={() => setAdding(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={saveEntry} disabled={isSaving}
                               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                           {isSaving ? 'Saving...' : 'Save entry'}
                       </button>
                   </>}>
                {adding && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-slate-700">Date</label>
                                <input type="date" value={adding.entry_date} onChange={e => setAdding(p => ({ ...p, entry_date: e.target.value }))}
                                       className="mt-1 w-full border rounded-md px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700">Type</label>
                                <select value={adding.category} onChange={e => setAdding(p => ({ ...p, category: e.target.value, account_id: '' }))}
                                        className="mt-1 w-full border rounded-md px-3 py-2">
                                    {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">
                                {CATEGORIES.find(c => c.key === adding.category)?.flow === 'in' ? 'Income account' : 'Expense account'}
                            </label>
                            <select value={adding.account_id} onChange={e => setAdding(p => ({ ...p, account_id: e.target.value }))}
                                    className="mt-1 w-full border rounded-md px-3 py-2">
                                <option value="">Select...</option>
                                {pickable.map(a => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-slate-700">Amount</label>
                                <input type="number" step="0.01" min="0" value={adding.amount}
                                       onChange={e => setAdding(p => ({ ...p, amount: e.target.value }))}
                                       className="mt-1 w-full border rounded-md px-3 py-2" placeholder="0.00" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700">Reference (check #, invoice)</label>
                                <input value={adding.reference} onChange={e => setAdding(p => ({ ...p, reference: e.target.value }))}
                                       className="mt-1 w-full border rounded-md px-3 py-2" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Memo</label>
                            <input value={adding.memo} onChange={e => setAdding(p => ({ ...p, memo: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" placeholder="What was this for?" />
                        </div>
                        <p className="text-xs text-slate-500">This books a balanced double-entry against the Operating Bank Account automatically.</p>
                    </div>
                )}
            </Modal>

            {/* View-entry modal */}
            <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing ? `Entry #${viewing.gl_entry_id}` : ''}>
                {viewing && (
                    <div>
                        <div className="text-sm text-slate-600 mb-3 space-y-0.5">
                            <p><span className="font-medium">Date:</span> {new Date(viewing.entry_date).toLocaleDateString()}</p>
                            <p><span className="font-medium">Category:</span> {viewing.category.replace(/_/g, ' ')}</p>
                            {viewing.reference && <p><span className="font-medium">Reference:</span> {viewing.reference}</p>}
                            {viewing.memo && <p><span className="font-medium">Memo:</span> {viewing.memo}</p>}
                        </div>
                        <table className="w-full text-sm border-t">
                            <thead className="text-xs text-slate-400 uppercase">
                                <tr><th className="text-left py-1">Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr>
                            </thead>
                            <tbody>
                                {viewing.lines.map(l => (
                                    <tr key={l.gl_line_id} className="border-b">
                                        <td className="py-1.5 text-slate-700">{l.code} {l.name}</td>
                                        <td className="text-right">{Number(l.debit) ? fmt(l.debit) : ''}</td>
                                        <td className="text-right">{Number(l.credit) ? fmt(l.credit) : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>
        </div>
    );
};

const Row = ({ label, value, bold, big, positive }) => (
    <div className={`flex justify-between py-1 ${bold ? 'font-semibold' : ''} ${big ? 'text-lg' : 'text-sm'}`}>
        <span className="text-slate-700">{label}</span>
        <span className={positive === false ? 'text-red-500' : positive === true ? 'text-green-600' : 'text-slate-800'}>
            {value == null ? '—' : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </span>
    </div>
);

const StatementRows = ({ rows }) => (
    rows.length === 0
        ? <p className="text-sm text-slate-400 py-1">None</p>
        : rows.map(r => <Row key={r.account_id} label={`${r.code} ${r.name}`} value={r.balance} />)
);

export default AccountingPage;
